/**
 * useProjectRoleCheck - Project role-based permission checking
 */

import { useMemo, useCallback } from 'react';
import type { ProjectInfo } from '@/features/projects/services/projectService';
import type { ProjectNode } from '@/shared/types/projectData';
import type { User } from '@/features/auth/AuthContext';
import {
  canActOnNode as canActOnNodeByAuthority,
  canLinkDependencyNodes as canLinkDependencyNodesByAuthority,
  type NodeAuthorityAction,
} from '@/features/gantt/utils/nodeAuthority';

interface UseProjectRoleCheckParams {
  availableProjects: ProjectInfo[];
  user: Pick<User, 'id' | 'role'> | null;
  nodes?: ProjectNode[];
}

export function useProjectRoleCheck({ availableProjects, user, nodes = [] }: UseProjectRoleCheckParams) {
  // Map: projectId -> role from server (owner|editor|viewer)
  const projectRoleMap = useMemo(() => {
    const map = new Map<string, string>();
    (availableProjects || []).forEach((p) => {
      if (p.projectId) {
        map.set(p.projectId, p.role || 'viewer');
      }
    });
    return map;
  }, [availableProjects]);

  const singleProjectId = useMemo(() => {
    if (projectRoleMap.size !== 1) return undefined;
    return Array.from(projectRoleMap.keys())[0];
  }, [projectRoleMap]);

  const inferProjectId = useCallback((
    projectId?: string,
    node?: Partial<ProjectNode> | null,
  ): string | undefined => {
    const cleanProjectId = (value?: string | null) => {
      const trimmed = value?.trim();
      return trimmed && trimmed !== 'default' ? trimmed : undefined;
    };

    const explicitProjectId = cleanProjectId(projectId);
    if (explicitProjectId) return explicitProjectId;

    const nodeProjectId = cleanProjectId(node?.projectId);
    if (nodeProjectId) return nodeProjectId;

    if (typeof node?.id === 'string' && node.id.includes(':')) {
      const [candidateProjectId] = node.id.split(':');
      if (candidateProjectId && projectRoleMap.has(candidateProjectId)) {
        return candidateProjectId;
      }
    }

    return singleProjectId;
  }, [projectRoleMap, singleProjectId]);

  // Helper: only OWNER/EDITOR can edit a project's tasks
  const canEditProject = useCallback((
    projectId: string | undefined,
    node?: Partial<ProjectNode> | null,
    action: NodeAuthorityAction = 'update',
  ) => {
    if (user?.role === 'admin') return true; // Admin bypass
    const inferredProjectId = inferProjectId(projectId, node);
    if (!inferredProjectId) return false;
    const role = projectRoleMap.get(inferredProjectId);
    if (node?.id) {
      return canActOnNodeByAuthority({ nodes, node, action, projectRole: role, user });
    }
    return role === 'owner' || role === 'editor';
  }, [inferProjectId, nodes, projectRoleMap, user]);

  const canActOnNode = useCallback((
    node: Partial<ProjectNode> | null | undefined,
    action: NodeAuthorityAction = 'update',
  ) => {
    const projectId = inferProjectId(undefined, node);
    if (!projectId) return false;
    return canEditProject(projectId, node, action);
  }, [canEditProject, inferProjectId]);

  const canLinkDependencyNodes = useCallback((
    fromNode: Partial<ProjectNode> | null | undefined,
    toNode: Partial<ProjectNode> | null | undefined,
  ) => {
    const fromProjectId = inferProjectId(undefined, fromNode);
    const toProjectId = inferProjectId(undefined, toNode);
    if (!fromProjectId || !toProjectId || fromProjectId !== toProjectId) {
      return false;
    }
    return canLinkDependencyNodesByAuthority({
      nodes,
      fromNode,
      toNode,
      projectRole: projectRoleMap.get(fromProjectId),
      user,
    });
  }, [inferProjectId, nodes, projectRoleMap, user]);

  const getProjectRole = useCallback((projectId: string | undefined) => {
    if (user?.role === 'admin') return 'owner';
    if (!projectId) return 'viewer';
    return projectRoleMap.get(projectId) || 'viewer';
  }, [projectRoleMap, user?.role]);

  return {
    projectRoleMap,
    canEditProject,
    canActOnNode,
    canLinkDependencyNodes,
    getProjectRole,
  };
}
